import { useEffect, useMemo, useState } from "react"

import { DEFAULT_LANGUAGE, formatStripeAmount, intlDisplayNames, useLocale } from "@bounce/i18n"

import type { Country } from "../../../libs/bounce/bounceSdk.types.gen"

export type UsePricingSelectorProps = {
  defaultCountry: string
  countries: Country[]
}

export type UsePricingSelectorOption = {
  countryCode: string
  countryName: string
  formattedPrice: string
}

const getCountryName = (countryCode: string, language = DEFAULT_LANGUAGE) => {
  const formatter = intlDisplayNames({ locale: language, options: { type: "region" } })

  try {
    return formatter.of(countryCode) ?? countryCode
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error getting formatted country name for "${countryCode}".`, error)

    return countryCode
  }
}

/**
 * Returns a selector for pricing information based on countries.
 * @param countries - An array of objects containing country, luggagePrice and currency information.
 * @returns An object containing the selected option, options array and onChange function.
 */
export const usePricingSelector = (props: UsePricingSelectorProps) => {
  const { countries } = props
  const { language, countryCode } = useLocale()
  const defaultCountry = props.defaultCountry || countryCode

  // Generate options array based on countries, the country name is generated by the Intl API.
  const options: UsePricingSelectorOption[] = useMemo(() => {
    const options = countries
      .map(({ country, lowestPrice, currency }) => ({
        countryCode: country,
        countryName: getCountryName(country, language),
        formattedPrice: formatStripeAmount(lowestPrice, currency, language),
      }))
      .sort((a, b) => a.countryName.localeCompare(b.countryName))

    return options
  }, [countries, language])

  const defaultOption = useMemo(
    () => options.find(option => option.countryCode === defaultCountry) || options[0],
    [options, defaultCountry],
  )
  const [selectedOption, setSelectedOption] = useState(defaultOption)

  // Reset the selected option when the default country changes.
  useEffect(() => {
    setSelectedOption(defaultOption)
  }, [defaultOption])

  const onChange = (value: string) => {
    const option = options.find(option => option.countryCode === value)
    if (!option) return

    setSelectedOption(option)
  }

  return {
    selectedOption,
    options,
    onChange,
  }
}
